import React, { useMemo, useRef, useState, useEffect } from "react";
import {
  useTable,
  useGlobalFilter,
  useFilters,
  usePagination,
} from "react-table";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import {
  getAlquileresActivos,
  nuevoAlquiler,
  updateAlquiler,
} from "../../../api/Alquileres";
import ModalAlquiler from "./ModalAlquiler";

// 🔹 Utilidad: calcular diferencia de días (sin usar new Date)
function diffDaysManual(fechaFin) {
  if (!fechaFin) return null;
  const [y, m, d] = fechaFin.split("-").map(Number);
  if (!y || !m || !d) return null;

  const hoy = new Date();
  const yyyy = hoy.getFullYear();
  const mm = hoy.getMonth() + 1;
  const dd = hoy.getDate();

  const diasHoy = yyyy * 365 + mm * 30 + dd;
  const diasFin = y * 365 + m * 30 + d;
  return diasFin - diasHoy;
}

// 🔹 Filtro por defecto
function DefaultColumnFilter({ column: { filterValue, setFilter, placeholder } }) {
  return (
    <input
      className="form-control form-control-sm"
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      placeholder={placeholder || "Filtrar..."}
      style={{ minWidth: 100 }}
    />
  );
}

export default function AlquileresActivos({ onRefreshItems, viewOnly = false }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const tableRef = useRef(null);

  // 🔹 Cargar alquileres activos
  const loadAlquileres = async () => {
    try {
      setLoading(true);
      const res = await getAlquileresActivos();
      setData(Array.isArray(res) ? res : []);
    } catch (err) {
      Swal.fire("Error", err.message || "Error al cargar alquileres", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAlquileres();
  }, []);

  // 🔹 Guardar (nuevo o edición) — solo accesible con acceso completo
  const handleSave = async (formData) => {
    try {
      if (selectedItem) {
        await updateAlquiler(selectedItem.idalquiler, formData);
        Swal.fire("Actualizado", "Alquiler actualizado correctamente", "success");
      } else {
        await nuevoAlquiler(formData);
        Swal.fire("Agregado", "Alquiler registrado correctamente", "success");
      }

      setShowModal(false);
      setSelectedItem(null);
      loadAlquileres();

      if (typeof onRefreshItems === "function") {
        onRefreshItems();
      }
    } catch (err) {
      Swal.fire("Error", err.message || "No se pudo guardar el alquiler", "error");
    }
  };

  // 🔹 Columnas base (sin Monto)
  const baseColumns = useMemo(
    () => [
      { Header: "ID", accessor: "idalquiler", className: "text-center", minWidth: 60 },
      { Header: "Código", accessor: "coditem", className: "text-center", minWidth: 80 },
      {
        Header: "Ítem",
        accessor: "nombreitem",
        minWidth: 200,
        Cell: ({ value }) => (
          <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {value || "-"}
          </div>
        ),
      },
      {
        Header: "Usuario",
        accessor: "usuario",
        minWidth: 160,
        Cell: ({ value }) => (
          <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {value || "-"}
          </div>
        ),
      },
      {
        Header: "Fecha Inicio",
        accessor: "fechainicio",
        className: "text-center",
        minWidth: 120,
      },
      {
        Header: "Fecha Fin",
        accessor: "fechafin",
        className: "text-center",
        minWidth: 160,
        Cell: ({ value }) => {
          if (!value) return "-";
          const dias = diffDaysManual(value);
          let textoFecha = value;
          let textoDias = "";
          let color = "#000";

          if (dias !== null) {
            if (dias > 0) textoDias = `(${dias} día${dias > 1 ? "s" : ""})`;
            else if (dias === 0) textoDias = "(Hoy)";
            else textoDias = `(vencido ${Math.abs(dias)} día${Math.abs(dias) > 1 ? "s" : ""})`;

            if (dias <= 5) color = "#dc3545";
          }

          const styleBase = {
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            lineHeight: "1.3",
            color,
            fontWeight: dias <= 5 ? 600 : 400,
          };

          return (
            <div style={styleBase}>
              {textoFecha}
              {textoDias && (
                <>
                  <br />
                  {textoDias}
                </>
              )}
            </div>
          );
        },
      },
      {
        Header: "Factura",
        accessor: "factura",
        className: "text-center",
        minWidth: 120,
      },
    ],
    []
  );

  // 🔹 Columna Monto — solo para acceso completo
  const montoColumn = useMemo(
    () => ({
      Header: "Monto (Gs)",
      accessor: "monto",
      className: "text-end",
      minWidth: 120,
      Cell: ({ value }) => (value ? value.toLocaleString("es-PY") : "-"),
    }),
    []
  );

  // 🔹 Columna Opciones
  const opcionesColumn = useMemo(
    () => ({
      Header: "Opciones",
      accessor: "acciones",
      disableFilters: true,
      disableGlobalFilter: true,
      className: "text-center",
      width: 140,
      Cell: ({ row }) => (
        <div className="text-center">
          <button
            className={`btn btn-sm ${viewOnly ? "btn-outline-warning" : "btn-outline-primary"}`}
            onClick={() => {
              setSelectedItem(row.original);
              setShowModal(viewOnly ? "view" : "edit");
            }}
            title={viewOnly ? "Ver detalle" : "Editar"}
          >
            <i className={`fas ${viewOnly ? "fa-eye" : "fa-pen"}`}></i>
          </button>
        </div>
      ),
    }),
    [viewOnly]
  );

  // 🔹 Armar columnas según permisos
  const columns = useMemo(() => {
    if (viewOnly) {
      return [...baseColumns, opcionesColumn];
    }
    return [...baseColumns, montoColumn, opcionesColumn];
  }, [viewOnly, baseColumns, montoColumn, opcionesColumn]);

  const defaultColumn = useMemo(() => ({ Filter: DefaultColumnFilter }), []);

  const tableInstance = useTable(
    { columns, data, defaultColumn, initialState: { pageSize: 15 } },
    useFilters,
    useGlobalFilter,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    state,
    page,
    rows,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    setGlobalFilter,
  } = tableInstance;

  const { globalFilter } = state;

  // 🔹 Exportar Excel
  const handleExportExcel = () => {
    if (!rows || rows.length === 0)
      return Swal.fire("Sin datos", "No hay datos para exportar", "info");

    const mapped = rows.map((r) => {
      const row = {
        ID: r.original.idalquiler,
        Código: r.original.coditem,
        Ítem: r.original.nombreitem,
        Usuario: r.original.usuario,
        "Fecha Inicio": r.original.fechainicio,
        "Fecha Fin": r.original.fechafin,
        Factura: r.original.factura,
        Estado: r.original.estado,
      };
      // Solo incluir Monto si tiene acceso completo
      if (!viewOnly) {
        row["Monto"] = r.original.monto;
      }
      return row;
    });

    const ws = XLSX.utils.json_to_sheet(mapped);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Alquileres Activos");
    XLSX.writeFile(wb, "AlquileresActivos.xlsx");
  };

  return (
    <div className="mt-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex gap-2">
          {/* 🔒 Solo acceso completo puede crear nuevos alquileres */}
          {!viewOnly && (
            <button
              className="btn btn-primary"
              onClick={() => {
                setSelectedItem(null);
                setShowModal("new");
              }}
            >
              + Nuevo Alquiler
            </button>
          )}
          <button
            className="btn btn-outline-success"
            onClick={handleExportExcel}
            disabled={loading || !rows || rows.length === 0}
          >
            Exportar Excel
          </button>
        </div>

        <div style={{ width: 250 }}>
          <input
            type="text"
            className="form-control"
            placeholder="Buscar en todo…"
            value={globalFilter || ""}
            onChange={(e) => setGlobalFilter(e.target.value)}
          />
        </div>
      </div>

      {/* Tabla */}
      {loading ? (
        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: "30vh" }}>
          <div
            style={{
              width: 38,
              height: 38,
              border: "4px solid #ddd",
              borderTop: "4px solid #1569c9ff",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          />
          <span style={{ marginLeft: 14, color: "#444", fontSize: 18 }}>
            Cargando alquileres...
          </span>
          <style>{`@keyframes spin{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}`}</style>
        </div>
      ) : (
        <div className="table-responsive">
          <table {...getTableProps()} className="table table-striped table-bordered table-sm">
            <thead>
              {headerGroups.map((headerGroup, i) => (
                <React.Fragment key={i}>
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th key={column.id}>{column.render("Header")}</th>
                    ))}
                  </tr>
                  <tr>
                    {headerGroup.headers.map((column) => (
                      <th key={`${column.id}-filter`}>
                        {column.canFilter ? column.render("Filter") : null}
                      </th>
                    ))}
                  </tr>
                </React.Fragment>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row, ri) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} key={ri}>
                    {row.cells.map((cell, ci) => (
                      <td {...cell.getCellProps()} key={ci}>
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Paginación */}
      <div className="d-flex justify-content-between align-items-center mt-2">
        <span>
          Registros: <strong>{rows?.length || 0}</strong>
        </span>
        <div>
          <button
            className="btn btn-outline-primary btn-sm me-1"
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
          >
            Anterior
          </button>
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={() => nextPage()}
            disabled={!canNextPage}
          >
            Siguiente
          </button>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <ModalAlquiler
          show={!!showModal}
          onHide={() => { setShowModal(false); setSelectedItem(null); }}
          onSave={handleSave}
          alquilerData={selectedItem}
          // viewOnly siempre true si el usuario es solo-lectura,
          // o true si se abrió en modo "view"
          viewOnly={viewOnly || showModal === "view"}
          hideMonto={viewOnly}
        />
      )}
    </div>
  );
}
